/**
 * Runs the first-run wizard OVER the app until this user has finished it.
 *
 * An overlay, not a block: children always mount. The wizard sequences existing
 * app surfaces rather than reimplementing them — `useRpcQuery`,
 * `SearchIndexingPanel`, `useAgentEngine`, `SourceConnect` — so it must sit
 * UNDER `AppRuntimeProvider`, and the app underneath is what those panels need
 * in order to work.
 */
import type { JSX, ReactNode } from "react";
import { useState } from "react";

import { useRpcQuery } from "../runtime/hooks/useRpc";
import { SetupFlow, type SetupState } from "./full/SetupFlow";
import { useOnboarding } from "./context";

export function FullGate({ children }: { readonly children: ReactNode }): JSX.Element {
  const [doneLocally, setDoneLocally] = useState(false);
  const onboarding = useOnboarding();
  const wanted = onboarding.stage === "full";

  // Only asked for when this user actually owes the wizard: a demo user, or
  // anyone already onboarded, has no business fetching wizard state.
  const { data, isLoading, isError } = useRpcQuery<SetupState>(
    ["setup", "get"],
    "setup.get",
    undefined,
    { enabled: wanted },
  );

  const show = wanted && !isLoading && !isError && !doneLocally && data?.completed !== true;

  // Resume where an interrupted run stopped. Restarting at Welcome replayed
  // every choice AND re-ran every install; the step was already persisted, it
  // was simply never read back.
  //
  // The stored value is handed over VERBATIM: resolving it needs to know
  // whether this run visits the sources screen, which needs the offer set,
  // which the channel publishes and only the wizard reads (DEC-21).
  const picks = data?.capabilities;

  return (
    <>
      {children}
      {show && (
        <SetupFlow
          initialStepRaw={data?.current_step ?? null}
          initialEngine={data?.engine ?? null}
          initialFailed={data?.deferred ?? []}
          {...(picks !== undefined && picks.length > 0 ? { initialPicks: picks } : {})}
          onFinished={() => {
            setDoneLocally(true);
          }}
        />
      )}
    </>
  );
}
